// Partner-workspace nav customization (per-user reorder + optional groups).
//
// A user arranges their Partner sidebar into ordered sections. An
// UNNAMED section (name === '') is the flat, header-less list — so a
// user who only reorders (never makes a group) has exactly one unnamed
// section. Named sections render a group header.
//
// The doc is persisted per-user via the `nav_layout` preference. On
// read we RECONCILE against the caller's live entitlement:
//   - a key the account no longer has → dropped,
//   - a newly-entitled key not in the doc → appended (95.6 pattern),
// so a product a library buys later just appears, and one they lose
// vanishes, without the stored doc going stale.

export interface NavSection {
	/** '' = the flat, header-less section. */
	name: string;
	/** Reorderable item keys, in display order. */
	items: string[];
}

export interface NavLayoutDoc {
	v: number;
	sections: NavSection[];
	/**
	 * Keys the user has hidden from the rail. A hidden key stays exactly
	 * where it is in `sections` — hiding never removes/reorders it — so
	 * restoring it puts it straight back where it was.
	 */
	hidden?: string[];
}

export const NAV_LAYOUT_VERSION = 1;

/** A single flat section holding the entitled keys in default order. */
export function defaultNavSections(entitled: string[]): NavSection[] {
	return [{ name: '', items: [...entitled] }];
}

export function docFromSections(sections: NavSection[]): NavLayoutDoc {
	return {
		v: NAV_LAYOUT_VERSION,
		sections: sections.map((s) => ({ name: s.name, items: [...s.items] })),
	};
}

/**
 * Reconcile a saved doc against the live entitled key set (in default
 * order). Drops keys not entitled, dedupes, and appends any entitled key
 * the doc doesn't already place. A null/empty doc yields the default
 * flat layout.
 */
export function applyNavLayout(entitled: string[], doc: NavLayoutDoc | null | undefined): NavSection[] {
	const entitledSet = new Set(entitled);
	const seen = new Set<string>();
	let sections: NavSection[] = [];

	if (doc && Array.isArray(doc.sections) && doc.sections.length > 0) {
		sections = doc.sections.map((s) => {
			const items = (Array.isArray(s.items) ? s.items : []).filter((k) => {
				if (!entitledSet.has(k) || seen.has(k)) return false;
				seen.add(k);
				return true;
			});
			return { name: typeof s.name === 'string' ? s.name : '', items };
		});
	}

	// Append entitled keys the doc never placed (new products), in
	// default order, to the last section (or a fresh flat section).
	const missing = entitled.filter((k) => !seen.has(k));
	if (missing.length > 0) {
		if (sections.length === 0) {
			sections = [{ name: '', items: missing }];
		} else {
			sections[sections.length - 1].items.push(...missing);
		}
	}
	return sections;
}

/**
 * The currently-hidden keys, reconciled against live entitlement (a key
 * the account no longer has drops out, same as it drops out of
 * `applyNavLayout`'s sections — a stale hidden key never lingers).
 */
export function hiddenNavKeys(entitled: string[], doc: NavLayoutDoc | null | undefined): Set<string> {
	const entitledSet = new Set(entitled);
	const hidden = doc && Array.isArray(doc.hidden) ? doc.hidden : [];
	const out = new Set<string>();
	for (const k of hidden) {
		if (typeof k === 'string' && entitledSet.has(k)) out.add(k);
	}
	return out;
}

/** Hide `key` from the rail. Its position in `sections` is untouched. */
export function hideNavKey(doc: NavLayoutDoc, key: string): NavLayoutDoc {
	const hidden = new Set(doc.hidden ?? []);
	hidden.add(key);
	return { ...doc, hidden: [...hidden] };
}

/** Restore a hidden key to visibility, in its existing position. */
export function showNavKey(doc: NavLayoutDoc, key: string): NavLayoutDoc {
	if (!doc.hidden || !doc.hidden.includes(key)) return doc;
	return { ...doc, hidden: doc.hidden.filter((k) => k !== key) };
}

/** Remove a key from every section (returns a fresh structure). */
function withoutKey(sections: NavSection[], key: string): NavSection[] {
	return sections.map((s) => ({ name: s.name, items: s.items.filter((k) => k !== key) }));
}

/**
 * Move `key` so it sits immediately before `targetKey` (within
 * targetKey's section). No-op if the target is missing or is the key
 * itself. Key-relative (not index-relative) so the caller never does
 * post-removal index math.
 */
export function moveNavItemBefore(sections: NavSection[], key: string, targetKey: string): NavSection[] {
	if (key === targetKey) return sections;
	const next = withoutKey(sections, key);
	for (const s of next) {
		const idx = s.items.indexOf(targetKey);
		if (idx !== -1) {
			s.items.splice(idx, 0, key);
			return next;
		}
	}
	return sections; // target not found → no move
}

/**
 * Move `key` so it sits immediately after `targetKey` (within
 * targetKey's section). Mirror of moveNavItemBefore, for a drop on the
 * bottom half of a row.
 */
export function moveNavItemAfter(sections: NavSection[], key: string, targetKey: string): NavSection[] {
	if (key === targetKey) return sections;
	const next = withoutKey(sections, key);
	for (const s of next) {
		const idx = s.items.indexOf(targetKey);
		if (idx !== -1) {
			s.items.splice(idx + 1, 0, key);
			return next;
		}
	}
	return sections; // target not found → no move
}

/** Move `key` to the end of section `sectionIdx`. */
export function moveNavItemToSectionEnd(sections: NavSection[], key: string, sectionIdx: number): NavSection[] {
	if (sectionIdx < 0 || sectionIdx >= sections.length) return sections;
	const next = withoutKey(sections, key);
	next[sectionIdx].items.push(key);
	return next;
}

/** Append a new empty named group. */
export function addNavGroup(sections: NavSection[], name = 'New group'): NavSection[] {
	return [...sections.map((s) => ({ name: s.name, items: [...s.items] })), { name, items: [] }];
}

/** Rename section `idx`. */
export function renameNavGroup(sections: NavSection[], idx: number, name: string): NavSection[] {
	return sections.map((s, i) => (i === idx ? { name, items: [...s.items] } : { name: s.name, items: [...s.items] }));
}

/**
 * Remove group `idx`, preserving its modules by merging them into the
 * previous section (or the next, or a new flat section) — a removed
 * group never loses a module.
 */
export function removeNavGroup(sections: NavSection[], idx: number): NavSection[] {
	if (idx < 0 || idx >= sections.length) return sections;
	const doomed = sections[idx];
	const rest = sections.filter((_, i) => i !== idx).map((s) => ({ name: s.name, items: [...s.items] }));
	if (doomed.items.length === 0) return rest;
	if (rest.length === 0) return [{ name: '', items: [...doomed.items] }];
	const mergeInto = idx > 0 ? idx - 1 : 0;
	rest[mergeInto].items.push(...doomed.items);
	return rest;
}
